import type { Avatar } from "@coup/shared";
import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router";
import { AvatarBuilder, randomPreset, type Preset } from "../components/AvatarBuilder";
import { Stepper } from "../components/Stepper";
import ui from "../components/ui.module.css";
import { getToken, loadProfile, saveProfile } from "../state/session";
import { useStore } from "../state/store";
import styles from "./Home.module.css";

export function Home() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const send = useStore((s) => s.send);
  const seatedCode = useStore((s) => s.code);

  const [saved] = useState(loadProfile);
  const [name, setName] = useState(saved?.name ?? "");
  const [preset, setPreset] = useState<Preset>(saved?.avatar ?? randomPreset);
  const [avatar, setAvatar] = useState<Avatar>(preset);
  const [seconds, setSeconds] = useState(5);
  const [code, setCode] = useState(params.get("code") ?? "");

  useEffect(() => {
    if (!seatedCode) return;
    // Keep other params (the dev-only ?as=) but drop ?code=, which the path now carries.
    const rest = new URLSearchParams(location.search);
    rest.delete("code");
    const query = rest.toString();
    navigate(`/${seatedCode}${query ? `?${query}` : ""}`);
  }, [seatedCode, navigate]);

  const trimmed = name.trim();
  const create = () => {
    saveProfile(trimmed, avatar);
    send({ t: "create", name: trimmed, avatar, responseMs: seconds * 1000 });
  };
  const join = () => {
    const c = code.trim().toUpperCase();
    saveProfile(trimmed, avatar);
    send({ t: "join", code: c, name: trimmed, avatar, token: getToken(c) });
  };

  return (
    <div className={styles.page}>
      <div className={styles.title}>
        <div className={styles.logo}>Coup</div>
      </div>

      <div className={styles.cards}>
        <section className={`${ui.panel} ${styles.who}`}>
          <div className={ui.heading}>Who are you?</div>
          <AvatarBuilder
            name={name}
            avatar={avatar}
            preset={preset}
            onName={setName}
            onAvatar={(a, p) => {
              setAvatar(a);
              setPreset(p);
            }}
          />
        </section>

        <section className={`${ui.panel} ${styles.tables}`}>
          <div className={ui.heading}>Host a table</div>
          <div className={styles.setting}>
            <div>
              <div className={styles.settingName}>Response window</div>
              <div className={styles.settingHint}>Time to call Bullshit or block, 3–15 s</div>
            </div>
            <Stepper seconds={seconds} onChange={setSeconds} />
          </div>
          <button type="button" className={ui.primary} disabled={!trimmed} onClick={create}>
            Create lobby
          </button>

          <div className={styles.or}>
            <div />
            or
            <div />
          </div>

          <div className={ui.heading}>Join a table</div>
          <form
            className={styles.joinField}
            onSubmit={(e) => {
              e.preventDefault();
              join();
            }}
          >
            <label htmlFor="code" className={ui.label}>
              Lobby code
            </label>
            <div className={styles.joinRow}>
              <input
                id="code"
                className={styles.code}
                maxLength={5}
                placeholder="KXRTM"
                value={code}
                onChange={(e) => setCode(e.target.value.toUpperCase())}
              />
              <button
                type="submit"
                className={styles.join}
                disabled={!trimmed || code.trim().length !== 5}
              >
                Join
              </button>
            </div>
            <div className={styles.settingHint}>
              Rejoining? Enter the same code to reclaim your seat.
            </div>
          </form>
        </section>
      </div>
    </div>
  );
}
